import { sanitize } from '@Services/HtmlSanitizeService';
import { citationLiteratureFormatter } from '@Services/FormatUtils';
import { extractRefsFromSummary } from '@Containers/Summary/summaryRefsUtil';

function decodePlainTextEntities(value) {
  return value.replace(/&gt;/g, '>').replace(/&lt;/g, '<').replace(/&amp;/g, '&');
}

function formatCitationDetails(citation) {
  const title = citation.title ? sanitize(citation.title, 'discard', { allowedTags: [], allowedAttributes: {} }) : '';

  const authorNames = citation.authors?.map((auth) => auth?.author).filter(Boolean) || [];
  const authors =
    authorNames.length <= 9 ? authorNames.join('; ') : [...authorNames.slice(0, 7), '(...)', ...authorNames.slice(-2)].join('; ');

  const JOURNAL_FIELDS_MAPPER = [
    { field: 'issueTitle' },
    { field: 'issueYear' },
    { field: 'issueVolume' },
    { field: 'issueNo' },
    { field: 'articleNo' },
    { field: 'issuePages' },
  ];

  const journalsLine = citation.citationJournals
    ? citation.citationJournals
        .map((journal) => {
          const parts = JOURNAL_FIELDS_MAPPER.reduce((acc, { field }) => {
            const value = journal?.[field];
            if (!value) return acc;
            acc.push(value);
            return acc;
          }, []);
          const html = parts.join(', ');
          return html ? sanitize(html, 'discard', { allowedTags: [], allowedAttributes: {} }) : '';
        })
        .filter(Boolean)
        .join('; ')
    : '';

  const assigneesLine = citation.assignees
    ? `Current patent assignee: ${sanitize(String(citation.assignees), 'discard', { allowedTags: [], allowedAttributes: {} })}`
    : '';

  const patentsLine = citation.citationPatents
    ? sanitize(citationLiteratureFormatter(citation.citationPatents), 'discard', { allowedTags: [], allowedAttributes: {} })
    : '';

  return [title, journalsLine, patentsLine, assigneesLine, authors].filter(Boolean);
}

function formatReferenceLine(citation, index) {
  if (!citation) return '';

  const [title, ...details] = formatCitationDetails(citation);

  return [`${index}. ${title || ''}`, ...details].filter(Boolean).join('\n');
}

function formatReactionReferenceLine(reaction, index) {
  if (!reaction) return '';

  const reactionId = reaction?.id ? sanitize(String(reaction.id), 'discard', { allowedTags: [], allowedAttributes: {} }) : '';
  const reactionText = reaction?.reaction
    ? decodePlainTextEntities(sanitize(String(reaction.reaction), 'discard', { allowedTags: [], allowedAttributes: {} }))
    : '';
  const citationDetails = reaction?.referencedCitation ? formatCitationDetails(reaction.referencedCitation) : [];

  return [`${index}. Reaction ID: ${reactionId}`, ...citationDetails, !citationDetails.length ? reactionText : '']
    .filter(Boolean)
    .join('\n');
}

export default function formatReferencesCopyToClipboard(summary, references, options = {}) {
  if (!Array.isArray(references) || !references.length) {
    return '';
  }

  // Map {[cnr]: index}
  const refsFromSummary = extractRefsFromSummary(summary, references);
  const formatLine = options.type === 'reactions' ? formatReactionReferenceLine : formatReferenceLine;

  const formattedReferences = references
    .filter((reference) => reference && refsFromSummary[reference.id])
    .map((reference) => formatLine(reference, refsFromSummary[reference.id]))
    .filter(Boolean)
    .join('\n\n');

  if (!formattedReferences) {
    return '';
  }

  return `\n\n---\n\n## References\n\n${formattedReferences}\n`;
}
